import React, { useState } from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaShoppingCart, FaHeart, FaRegHeart } from 'react-icons/fa';
import useCartStore from '../store/cartStore';
import useWishlistStore from '../store/wishlistStore';
import { getImageUrl } from '../utils/imageUrl';
import { toast } from 'react-toastify';

const ProductCard = ({ product }) => {
  const { addToCart } = useCartStore();
  const { toggleWishlist, isInWishlist } = useWishlistStore();
  const [imageLoaded, setImageLoaded] = useState(false);
  const inWishlist = isInWishlist(product._id);

  const handleAddToCart = () => {
    addToCart(product);
    toast.success('Product added to cart!');
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleWishlist(product);
    if (added) {
      toast.success('Added to wishlist!');
    } else {
      toast.info('Removed from wishlist');
    }
  };

  const getLowestPrice = () => {
    if (product.priceTiers && product.priceTiers.length > 0) {
      const prices = product.priceTiers.map(tier => tier.pricePerUnit);
      return Math.min(...prices, product.basePrice);
    }
    return product.basePrice;
  };

  const getDiscountPercentage = () => {
    if (product.originalPrice && product.originalPrice > product.basePrice) {
      return Math.round(((product.originalPrice - product.basePrice) / product.originalPrice) * 100);
    }
    return 0;
  };

  const imageUrl = getImageUrl(product.images?.[0] || product.image);
  const discountPercent = getDiscountPercentage();

  return (
    <Card className="product-card h-100 shadow-sm position-relative">
      {/* Badges Overlay */}
      <div className="position-absolute top-0 start-0 p-2" style={{ zIndex: 1 }}>
        {product.badges?.isNew && <Badge bg="success" className="d-block mb-1">New</Badge>}
        {product.badges?.isPopular && <Badge bg="warning" className="d-block mb-1">Popular</Badge>}
        {product.badges?.isBestSeller && <Badge bg="danger" className="d-block mb-1">Best Seller</Badge>}
      </div>
      
      {/* Discount Badge */}
      {discountPercent > 0 && (
        <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 1 }}>
          <Badge bg="danger" className="fs-6">{discountPercent}% OFF</Badge>
        </div>
      )}

      {/* Wishlist Button */}
      <Button
        variant="link"
        className="position-absolute p-2"
        style={{ top: '5px', right: '5px', zIndex: 2 }}
        onClick={handleToggleWishlist}
        title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        {inWishlist ? (
          <FaHeart size={20} className="text-danger" />
        ) : (
          <FaRegHeart size={20} className="text-secondary" />
        )}
      </Button>
      
      {/* Lazy loaded image with placeholder */}
      <div style={{ height: '200px', backgroundColor: '#f0f0f0', position: 'relative' }}>
        <Card.Img 
          variant="top" 
          src={imageUrl} 
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          style={{ 
            height: '200px', 
            objectFit: 'cover',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
        {!imageLoaded && (
          <div 
            className="position-absolute top-50 start-50 translate-middle text-muted"
            style={{ fontSize: '14px' }}
          >
            Loading...
          </div>
        )}
      </div>
      <Card.Body className="d-flex flex-column">
        <div className="mb-2">
          <Badge bg="info" className="category-badge">{product.category}</Badge>
          {product.featured && <Badge bg="warning" className="ms-1">Featured</Badge>}
        </div>
        <Card.Title>{product.name}</Card.Title>
        <Card.Text className="text-muted small flex-grow-1">
          {product.shortDescription}
        </Card.Text>
        <div className="mt-auto">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              {product.originalPrice && product.originalPrice > product.basePrice ? (
                <>
                  <div>
                    <span className="text-decoration-line-through text-muted me-2">₹{product.originalPrice}</span>
                    <h5 className="d-inline mb-0 text-primary">₹{product.basePrice}</h5>
                  </div>
                  {product.priceTiers && product.priceTiers.length > 0 && (
                    <small className="text-success">Bulk: From ₹{getLowestPrice()}</small>
                  )}
                </>
              ) : (
                <>
                  <h5 className="mb-0 text-primary">₹{product.basePrice}</h5>
                  {product.priceTiers && product.priceTiers.length > 0 && (
                    <small className="text-success">Bulk: From ₹{getLowestPrice()}</small>
                  )}
                </>
              )}
            </div>
            <Badge bg={product.stockCount > 0 ? 'success' : 'danger'}>
              {product.stockCount > 0 ? `${product.stockCount} in stock` : 'Out of stock'}
            </Badge>
          </div>
          <div className="d-grid gap-2">
            <Button 
              as={Link} 
              to={`/products/${product._id}`} 
              variant="outline-primary" 
              size="sm"
            >
              View Details
            </Button>
            <Button 
              variant="primary" 
              size="sm" 
              onClick={handleAddToCart}
              disabled={product.stockCount === 0}
            >
              <FaShoppingCart /> Add to Cart
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductCard;
